import React, { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import Layout from "../components/layouts/Layout";
import { syncAdminPermissions } from "../redux/authAdapter";
import { getUserErrorMessage } from "../api/apiErrors";
import { showError } from "../utils/alert";
import { shouldRefreshAdminPermissions } from "../utils/adminPermissions";

type Props = {
  children: React.ReactNode;
  redirectLink?: string;
  title?: string;
  headline?: string;
};

function ProtectedRouteAdmin({ children, redirectLink, title = "Admin", headline = "" }: Props) {
  const { accessToken, role } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);

  const currentPath = encodeURIComponent(location.pathname + location.search);

  useEffect(() => {
    if (role !== "ADMIN" || !accessToken) {
      previousPathRef.current = location.pathname;
      return;
    }

    const shouldRefresh = shouldRefreshAdminPermissions(previousPathRef.current, location.pathname);
    previousPathRef.current = location.pathname;

    if (!shouldRefresh) return;

    let isMounted = true;

    const refreshPermissions = async () => {
      try {
        await dispatch(syncAdminPermissions() as any);
      } catch (error) {
        if (isMounted) {
          showError(getUserErrorMessage(error));
        }
      }
    };

    void refreshPermissions();

    return () => {
      isMounted = false;
    };
  }, [accessToken, dispatch, location.pathname, role]);

  if (role === "ADMIN" && accessToken) {
    return (
      <Layout title={title} headline={headline}>
        {children}
      </Layout>
    );
  }

  if (role === "ORGANIZER") return <Navigate to="/organizer" replace />;
  if (role === "STORE") return <Navigate to="/store/dashboard" replace />;
  if (role === "OWNER") return <Navigate to="/owner" replace />;

  return (
    <Navigate
      to={redirectLink ? redirectLink : `/admin/send-otp?redirect=${currentPath}`}
      replace
    />
  );
}

export default ProtectedRouteAdmin;
